import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyAiCost,
  TechnologyAiCostUpsertPayload,
  TechnologyAiUsage,
  createTechnologyAiCost,
  deleteTechnologyAiCost,
  listTechnologyAiCosts,
  listTechnologyAiUsages,
  updateTechnologyAiCost,
} from '../../services/technologyApi';

const PAGE_SIZE = 10;

const MONTH_OPTIONS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

function currentPeriod(): { year: number; month: number } {
  const date = new Date();
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

function emptyCostForm(): TechnologyAiCostUpsertPayload {
  const period = currentPeriod();
  return {
    usageId: '',
    periodYear: period.year,
    periodMonth: period.month,
    tokenCount: null,
    amount: 0,
    currency: 'BRL',
    budgetAmount: null,
    notes: '',
  };
}

function readProblemDetail(error: unknown): string | null {
  if (typeof error !== 'object' || error == null || !('response' in error)) {
    return null;
  }

  const response = (error as { response?: { data?: { detail?: string } } }).response;
  return response?.data?.detail ?? null;
}

function optionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function monthLabel(month: number): string {
  return MONTH_OPTIONS.find((option) => option.value === month)?.label ?? String(month);
}

export function TechnologyAiCostsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.ai-costs');
  const [items, setItems] = useState<TechnologyAiCost[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyAiCostUpsertPayload>(emptyCostForm);
  const [amountText, setAmountText] = useState('');
  const [tokenText, setTokenText] = useState('');
  const [budgetText, setBudgetText] = useState('');
  const [saving, setSaving] = useState(false);
  const [usages, setUsages] = useState<TechnologyAiUsage[]>([]);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyAiCosts({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao carregar custos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    void listTechnologyAiUsages({ page: 1, pageSize: 200 })
      .then((result) => setUsages(result.items))
      .catch(() => setUsages([]));
  }, []);

  function resetForm(): void {
    setEditingId(null);
    setAmountText('');
    setTokenText('');
    setBudgetText('');
    setForm(emptyCostForm());
  }

  function startEdit(item: TechnologyAiCost): void {
    expandForm();
    setEditingId(item.id);
    setAmountText(String(item.amount));
    setTokenText(item.tokenCount == null ? '' : String(item.tokenCount));
    setBudgetText(item.budgetAmount == null ? '' : String(item.budgetAmount));
    setForm({
      usageId: item.usageId,
      periodYear: item.periodYear,
      periodMonth: item.periodMonth,
      tokenCount: item.tokenCount ?? null,
      amount: item.amount,
      currency: item.currency,
      budgetAmount: item.budgetAmount ?? null,
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const amount = optionalNumber(amountText);
    if (amount == null) {
      toast.error('Informe o valor.');
      return;
    }

    const payload: TechnologyAiCostUpsertPayload = {
      ...form,
      amount,
      tokenCount: optionalNumber(tokenText),
      budgetAmount: optionalNumber(budgetText),
      currency: form.currency.trim().toUpperCase() || 'BRL',
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyAiCost(editingId, payload);
        toast.success('Custo atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyAiCost(payload);
        toast.success('Custo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao salvar custo.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyAiCost): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir custo',
      message: `Deseja excluir o lançamento de ${monthLabel(item.periodMonth)}/${item.periodYear}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyAiCost(item.id);
      toast.success('Custo excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch (error) {
      toast.error(readProblemDetail(error) ?? 'Falha ao excluir custo.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Custos de IA"
        description="Gasto mensal de cada uso de IA. Um lançamento por mês. O valor acima do orçamento fica marcado."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar custo' : 'Novo custo'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${monthLabel(form.periodMonth)}/${form.periodYear}`
              : undefined
          }
          createSummary="Clique para expandir e lançar um custo mensal"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Uso de IA
                <select
                  className="erp-input"
                  value={form.usageId}
                  onChange={(event) => setForm({ ...form, usageId: event.target.value })}
                  required
                >
                  <option value="">Selecione</option>
                  {usages.map((usage) => (
                    <option key={usage.id} value={usage.id}>
                      {usage.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Ano
                <input
                  className="erp-input"
                  inputMode="numeric"
                  value={form.periodYear}
                  onChange={(event) =>
                    setForm({ ...form, periodYear: Number(event.target.value) || form.periodYear })
                  }
                  required
                />
              </label>
              <label className="erp-label">
                Mês
                <select
                  className="erp-input"
                  value={form.periodMonth}
                  onChange={(event) =>
                    setForm({ ...form, periodMonth: Number(event.target.value) })
                  }
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Tokens
                <input
                  className="erp-input"
                  inputMode="numeric"
                  value={tokenText}
                  onChange={(event) => setTokenText(event.target.value)}
                />
              </label>
              <label className="erp-label">
                Valor
                <input
                  className="erp-input"
                  inputMode="decimal"
                  value={amountText}
                  onChange={(event) => setAmountText(event.target.value)}
                  required
                />
              </label>
              <label className="erp-label">
                Moeda
                <input
                  className="erp-input"
                  maxLength={3}
                  value={form.currency}
                  onChange={(event) => setForm({ ...form, currency: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Orçamento
                <input
                  className="erp-input"
                  inputMode="decimal"
                  value={budgetText}
                  onChange={(event) => setBudgetText(event.target.value)}
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por uso ou observação"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Uso</th>
                  <th>Período</th>
                  <th>Tokens</th>
                  <th>Valor</th>
                  <th>Orçamento</th>
                  <th>Situação</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 7 : 6} className="text-center text-secondary-500">
                      Nenhum custo cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.usageName || '—'}</td>
                      <td>
                        {monthLabel(item.periodMonth)}/{item.periodYear}
                      </td>
                      <td>{item.tokenCount ?? '—'}</td>
                      <td>
                        {item.amount} {item.currency}
                      </td>
                      <td>{item.budgetAmount == null ? '—' : `${item.budgetAmount} ${item.currency}`}</td>
                      <td>
                        {item.budgetAmount == null ? (
                          '—'
                        ) : item.overBudget ? (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                            Acima do orçamento
                          </span>
                        ) : (
                          'Dentro do orçamento'
                        )}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
